import React, { useState, useMemo } from "react";
import { Dropdown } from "./PrimereactComponents";
import { useParams } from "react-router-dom";
import { useMunicipalityId } from '../hooks/useCouncilPath';
import { useEffect } from "react";
import { getApiClient, DropdownOptionsCode, PartyStats } from "../api";
import PartyBarChart from './charts/PartyBarChart';
import { redirectErrorPage } from "../utils/errors";
import { useTranslation } from "react-i18next";
import "./summaryVotesGraph.css"
import { capitalizeFirstLetter, useFormatters  } from "../utils/texts";
import { useLocalizationLabels } from "../context/LocalizationLabelContext";
import { truncateLegendLabel } from "../utils/nivoTheme";

type DropdownOptionsCodeName = DropdownOptionsCode & {
    name: string;
};

function SummaryVotesGraph() {
  const [partiesStats, setPartiesStats] = useState<PartyStats[]>([]);
  const [voteOptions, setVoteOptions] = useState<DropdownOptionsCode[]>([]);
  const [selectedVoteOption, setSelectedVoteOption] = useState<DropdownOptionsCodeName | null>(null);
  const [error, setError] = useState<Error | null>(null);
  
  const { bodyType, zastupitelstvoId } = useParams();
  const municipalitaId = useMunicipalityId();

  const { t, i18n} = useTranslation('votings');
  const currentLocale = i18n.language;
  const formatter = useFormatters(currentLocale);

  const {voteOptionLabels} = useLocalizationLabels();

  useEffect(() => {
    const fetchData = async () => {
        try {
            const api = getApiClient();
            const councilId = parseInt(zastupitelstvoId ?? '0', 10);
            const [partiesStatsData, voteOptionsData] = await Promise.all([
                api.getPartiesStatsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType ?? '', councilId),
                api.getDropdownVoteOptionsByMunicipalityIdAndBody(municipalitaId, bodyType ?? ''),
            ]);

            setPartiesStats(partiesStatsData);
            setVoteOptions(voteOptionsData);
        } catch (error) {
          if (error instanceof Error) {
            setError(error);
          } else {
            setError(new Error("fetchData: Unknown error"));
          }
          redirectErrorPage(error);
        }
    };

    fetchData();
  }, [municipalitaId, bodyType, zastupitelstvoId]);

  const dropdownVoteOptions = useMemo(() => {
    return (voteOptions ?? []).map((option) => ({
      id: option.id,
      code: option.code,
      name: capitalizeFirstLetter(voteOptionLabels[option.id]?.label_universal ?? option.code)
    }));
  }, [voteOptions, voteOptionLabels]);

  useEffect(() => {
    if (!dropdownVoteOptions.length) return;

    setSelectedVoteOption((prev) => {
      if (!prev) return dropdownVoteOptions[0];
      return dropdownVoteOptions.find((option) => option.id === prev.id) ?? dropdownVoteOptions[0];
    });
  }, [dropdownVoteOptions]);

  function onOptionChange(e: { value: DropdownOptionsCodeName }) {
    setSelectedVoteOption(e.value);
  }

  const summaryVotes = (partiesStats ?? []).map((party) => {
    const voteItem = (party.celkove_hlasy ?? []).find((item) => item.code === selectedVoteOption?.code);
    return { label: party.strana_zkratka, color: party.strana_barva, value: voteItem?.count || 0 };
  });

  if (!partiesStats || !selectedVoteOption) return null;

    if(partiesStats !== undefined && dropdownVoteOptions !== undefined && voteOptionLabels !== undefined){
    return (
        <div className="summary-votes">
            <div className="dropdown">
                <label htmlFor="summary-votes-vote-option">{t("votingOption")}</label>
                <span className="summary-votes-graph__dropdown">
                    <Dropdown
                      id="summary-votes-vote-option"
                      value={selectedVoteOption}
                      options={dropdownVoteOptions}
                      onChange={onOptionChange}
                      optionLabel="name"
                    />
                </span>
            </div>
          <PartyBarChart
            items={summaryVotes}
            height="250px"
            axisFormat={(value) => formatter.number.format(Number(value))}
            axisTitle={t('votesCount')}
            legendLabel={(d) => `${truncateLegendLabel(d.label, 16)} (${formatter.number.format(d.value)})`}
            legendItemWidth={190}
            maxLabelLength={40}
          />
      </div>
    );} else if(error){
      redirectErrorPage(error);
    } else{
      return null;
    }
  };

export default SummaryVotesGraph;
